import { useMutation, useQuery } from "./urqlHooks";

const GET_ACCOUNT_CHART = `
  query GET_ACCOUNT_CHART($companyId: String!) {
    accountChart(companyId: $companyId) {
      id
      _id
      companyId
      accounts {
        key
        label
        name
        creditCardIds
        passwordIds
        accountTag
        consolidationGroup
        category {
          key
          label
          name
        }
        children {
          key
          label
          name
          creditCardIds
          passwordIds
          accountTag
          consolidationGroup
          category {
            key
            label
            name
          }
          children {
            key
            label
            name
            creditCardIds
            passwordIds
            accountTag
            consolidationGroup
            category {
              key
              label
              name
            }
            children {
              key
              label
              name
              creditCardIds
              passwordIds
              accountTag
              consolidationGroup
              category {
                key
                label
                name
              }
            }
          }
        }
      }
      editionHistory {
        date
        userId
        userEmail
        event
        fields {
          key
          value
          oldValue
        }
      }
    }
  }
`;

export const GetAccountChart = (variables) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_ACCOUNT_CHART,
    variables,
    requestPolicy: 'network-only',
    pause: !variables.companyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_ACCOUNT_CHART_CATEGORY_OPTIONS = `
  query GET_ACCOUNT_CHART_CATEGORY_OPTIONS {
    accountChartCategoryOptions {
      sectionKey
      categories {
        key
        label
        name
      }
    }
  }
`;

export const useAccountChartCategoryOptions = () => {
  const [result] = useQuery({
    query: GET_ACCOUNT_CHART_CATEGORY_OPTIONS,
    requestPolicy: 'cache-first',
  });
  const sections = result.data?.accountChartCategoryOptions ?? [];
  const categoryOptionsBySection = sections.reduce((acc, s) => {
    acc[s.sectionKey] = s.categories ?? [];
    return acc;
  }, {});
  return { categoryOptionsBySection, fetching: result.fetching };
};

const GET_ACCOUNT_SYSTEM_TAG_OPTIONS = `
  query GET_ACCOUNT_SYSTEM_TAG_OPTIONS {
    accountSystemTagOptions {
      key
      label
      defaultStandardKey
    }
  }
`;

export const useAccountSystemTagOptions = () => {
  const [result] = useQuery({
    query: GET_ACCOUNT_SYSTEM_TAG_OPTIONS,
    requestPolicy: 'cache-first',
  });
  const systemTagOptions = (result.data?.accountSystemTagOptions ?? []).map((tag) => ({
    key: tag.key,
    label: tag.label,
    name: tag.label,
    defaultStandardKey: tag.defaultStandardKey,
  }));
  return { systemTagOptions, fetching: result.fetching };
};

const UPDATE_ACCOUNT_CHART = `
  mutation UPDATE_ACCOUNT_CHART($companyId: String!, $input: UpdateAccountChartInput!) {
    updateAccountChart(companyId: $companyId, input: $input) {
      id
      _id
      companyId
      accounts {
        key
        label
        name
        creditCardIds
        passwordIds
        accountTag
        consolidationGroup
        category {
          key
          label
          name
        }
        children {
          key
          label
          name
          creditCardIds
          passwordIds
          accountTag
          consolidationGroup
          category {
            key
            label
            name
          }
          children {
            key
            label
            name
            creditCardIds
            passwordIds
            accountTag
            consolidationGroup
            category {
              key
              label
              name
            }
            children {
              key
              label
              name
              creditCardIds
              passwordIds
              accountTag
              consolidationGroup
              category {
                key
                label
                name
              }
            }
          }
        }
      }
      editionHistory {
        date
        userId
        userEmail
        event
        fields {
          key
          value
          oldValue
        }
      }
    }
  }
`;

export const UpdateAccountChart = () => {
  const [result, executeMutation] = useMutation(UPDATE_ACCOUNT_CHART);
  return { ...result, executeMutation };
};

const CREATE_ACCOUNT = `
  mutation CREATE_ACCOUNT($companyId: String!, $parentKey: String, $name: String!) {
    createAccount(companyId: $companyId, parentKey: $parentKey, name: $name) {
      id
      _id
      companyId
      accounts {
        key
        label
        name
        creditCardIds
        passwordIds
        accountTag
        consolidationGroup
        category {
          key
          label
          name
        }
        children {
          key
          label
          name
          creditCardIds
          passwordIds
          accountTag
          consolidationGroup
          category {
            key
            label
            name
          }
          children {
            key
            label
            name
            creditCardIds
            passwordIds
            accountTag
            consolidationGroup
            category {
              key
              label
              name
            }
            children {
              key
              label
              name
              creditCardIds
              passwordIds
              accountTag
              consolidationGroup
              category {
                key
                label
                name
              }
            }
          }
        }
      }
      editionHistory {
        date
        userId
        userEmail
        event
        fields {
          key
          value
          oldValue
        }
      }
    }
  }
`;

export const CreateAccount = () => {
  const [result, executeMutation] = useMutation(CREATE_ACCOUNT);
  return { ...result, executeMutation };
};

const IMPORT_ACCOUNT_CHART = `
  mutation IMPORT_ACCOUNT_CHART($companyId: String!, $accounts: [AccountNodeInput!]!) {
    importAccountChart(companyId: $companyId, accounts: $accounts) {
      id
      _id
      companyId
      accounts {
        key
        label
        name
        creditCardIds
        passwordIds
        accountTag
        consolidationGroup
        category {
          key
          label
          name
        }
        children {
          key
          label
          name
          creditCardIds
          passwordIds
          accountTag
          consolidationGroup
          category {
            key
            label
            name
          }
          children {
            key
            label
            name
            creditCardIds
            passwordIds
            accountTag
            consolidationGroup
            category {
              key
              label
              name
            }
            children {
              key
              label
              name
              creditCardIds
              passwordIds
              accountTag
              consolidationGroup
              category {
                key
                label
                name
              }
            }
          }
        }
      }
      editionHistory {
        date
        userId
        userEmail
        event
        fields {
          key
          value
          oldValue
        }
      }
    }
  }
`;

export const ImportAccountChartMutation = () => {
  const [result, executeMutation] = useMutation(IMPORT_ACCOUNT_CHART);
  return { ...result, executeMutation };
};
